import { AssociationMember } from "@boark/shared/types/association-member";
import { RoleContact } from "@boark/shared/types/role-contact";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo } from "react";

import { useLocale } from "../stores/locale";
import { recordIdToId } from "../utils/record-id-to-id";
import { Button } from "./button";
import { Checkbox } from "./checkbox";
import { Input } from "./input";
import { TinyMCE } from "./tinymce";

export type RoleFormValues = {
  name: string;
  description: string;
  externalDescription: string;
  contact: {
    email: string;
    phone: string;
  };
  assignedUserIds: string[];
};

export function RoleForm({
  onCancel,
  onChange,
  onSubmit,
  values,
  members = [],
  loading = false,
  disabled = false,
  hasError = false,
  editorKey,
}: {
  onCancel: () => void;
  onChange: (values: RoleFormValues) => void;
  onSubmit: () => void;
  values: RoleFormValues;
  members?: AssociationMember[];
  loading?: boolean;
  disabled?: boolean;
  hasError?: boolean;
  editorKey?: string;
}) {
  const t = useLocale((state) => state.locale);
  const isValid = useMemo(() => values.name.trim() !== "", [values.name]);
  const isDisabled = disabled || loading;

  function toggleAssignedUser(userId: string, checked: boolean) {
    const normalizedId = recordIdToId(userId);
    const assignedUserIds = checked
      ? [...values.assignedUserIds, normalizedId]
      : values.assignedUserIds.filter((id) => id !== normalizedId);

    onChange({ ...values, assignedUserIds });
  }

  return (
    <form
      className="flex-1 flex flex-col justify-between border-1 border-gray-300 rounded-xs p-4 min-w-md"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="flex flex-col gap-4">
        <Input
          required
          disabled={isDisabled}
          label={t.role.name}
          type="text"
          value={values.name}
          onChange={(e) => onChange({ ...values, name: e.target.value })}
        />
        <div>
          <label
            className={`block text-sm font-medium mb-1 ${isDisabled ? "text-gray-400" : "text-gray-500"}`}
          >
            {t.role.description}
          </label>
          {editorKey && (
            <TinyMCE
              key={`${editorKey}-description`}
              value={values.description}
              onChange={(_event, editor) => {
                onChange({ ...values, description: editor.getContent() });
              }}
            />
          )}
        </div>
        <div>
          <label
            className={`block text-sm font-medium mb-1 ${isDisabled ? "text-gray-400" : "text-gray-500"}`}
          >
            {t.role.externalDescription}
          </label>
          {editorKey && (
            <TinyMCE
              key={`${editorKey}-external-description`}
              value={values.externalDescription}
              onChange={(_event, editor) => {
                onChange({
                  ...values,
                  externalDescription: editor.getContent(),
                });
              }}
            />
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-500 mb-2">
            {t.role.contact}
          </label>
          <div className="grid grid-cols-2 gap-2">
            <Input
              disabled={isDisabled}
              label={t.person.email}
              type="email"
              value={values.contact.email}
              onChange={(e) =>
                onChange({
                  ...values,
                  contact: { ...values.contact, email: e.target.value },
                })
              }
            />
            <Input
              disabled={isDisabled}
              label={t.person.phone}
              type="tel"
              value={values.contact.phone}
              onChange={(e) =>
                onChange({
                  ...values,
                  contact: { ...values.contact, phone: e.target.value },
                })
              }
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-500 mb-2">
            {t.role.assignedTo}
          </label>
          {members.length === 0 ? (
            <p className="text-gray-400 text-sm">{t.role.assignedToEmpty}</p>
          ) : (
            <ul className="border border-gray-300 rounded-xs divide-y">
              {members.map((member) => {
                const memberId = recordIdToId(member.id);

                return (
                  <li key={memberId} className="p-2">
                    <Checkbox
                      checked={values.assignedUserIds.includes(memberId)}
                      disabled={isDisabled}
                      label={member.email}
                      onClick={(checked) =>
                        toggleAssignedUser(member.id, !checked)
                      }
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {hasError && (
        <div className="flex items-center gap-2 text-red-600 mt-4">
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <span>{t.role.error}</span>
        </div>
      )}

      <div className="flex justify-end gap-2 mt-4">
        <Button disabled={isDisabled} type="button" onClick={onCancel}>
          {t.cancel}
        </Button>
        <Button disabled={isDisabled || !isValid} loading={loading} type="submit">
          {t.send.submit}
        </Button>
      </div>
    </form>
  );
}

export function getRoleContactFromForm(
  contact: RoleFormValues["contact"],
): RoleContact | undefined {
  if (!contact.email && !contact.phone) {
    return undefined;
  }

  return {
    ...(contact.email && { email: contact.email }),
    ...(contact.phone && { phone: contact.phone }),
  };
}
